import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import { IdleService } from './business/impl/idleService';
import { SharedProfileData } from './data/shared-profile-data';

@Injectable({
  providedIn: 'root', // Makes this service available application-wide
})
export class AuthService {
  constructor(
    private router: Router,
    private idleService: IdleService,
    private sharedProfileData: SharedProfileData,
  ) {}

  /**
   * Clears in-memory app session state. Does not remove Cognito tokens — use
   * {@link signOutUser} for full logout.
   */
  clearApplicationSession(): void {
    this.sharedProfileData.logout();
  }

  /**
   * Called when a user successfully signs in. Clears any stale profile cache
   * from a previous session/user so the next getProfile() loads fresh data.
   */
  onUserSignedIn(): void {
    this.clearApplicationSession();
  }

  async getCurrentAuthenticatedUser() {
    try {
      const currentAuthenticatedUser = await getCurrentUser();
      return currentAuthenticatedUser;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async getAccessTokens() {
    try {
      const authSession = await fetchAuthSession();
      return authSession.tokens;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async signOutUser() {
    try {
      this.idleService.stopWatching();
      console.log('IdleService stopped');

      this.clearApplicationSession();

      await signOut();
      console.log('SignOut successful');

      // Removes Cognito/Amplify tokens and any other persisted client state.
      localStorage.clear();
      sessionStorage.clear();
      console.log('Storage cleared');

      // Wait for navigation to complete so callers can safely reload.
      await this.router.navigateByUrl('/authentication/login', {
        replaceUrl: true,
      });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  async isSessionValid(): Promise<boolean> {
    try {
      const user = await getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  }
}
